import { Fragment } from "react";
import { useLocalization } from "../../hooks/useLocalization";
import { secondsToTimeParts } from "../../hooks/useTimer";
import { UpcomingMatch } from "../../types/types";
import { formatMatchDate } from "../../utils/formatMatchDate";
import { CommonTwitchLink } from "../shared/CommonTwitchLink";
import { TeamLogo } from "../shared/TeamLogo";

interface HeroCountdownProps {
  remainingSeconds: number;
  loading: boolean;
  failed: boolean;
  noContent: boolean;
  nextUpcomingMatch?: UpcomingMatch;
  liveMatches: UpcomingMatch[];
}

export const HeroCountdown = ({
  remainingSeconds,
  loading,
  failed,
  noContent,
  nextUpcomingMatch,
  liveMatches,
}: HeroCountdownProps) => {
  const { days, hours, minutes, seconds } = secondsToTimeParts(remainingSeconds);
  const { strings, locale } = useLocalization();
  const timerAtZero = days === 0 && hours === 0 && minutes === 0 && seconds === 0;
  const nextMatchTime = nextUpcomingMatch
    ? formatMatchDate(nextUpcomingMatch.time, locale, strings)
    : null;
  const isLive = liveMatches.length > 0 || timerAtZero;
  const showNextMatch = !isLive && nextUpcomingMatch && nextMatchTime;
  const showLiveMatches = isLive && liveMatches.length > 0;
  const hasMultipleLiveMatches = liveMatches.length > 1;

  if (loading || failed) {
    return <div className="countdown-shell" aria-hidden="true" />;
  }

  if (noContent) {
    return (
      <p className="countdown-shell countdown-motion m-0 text-center font-shentox text-[clamp(1.75rem,3.5vw,2.5rem)] tracking-[0.08em] uppercase">
        {strings.TRACKER.NO_CONTENT}
      </p>
    );
  }

  if (isLive) {
    return (
      <div className="countdown-shell countdown-motion flex-col gap-3 max-md:gap-2">
        {showLiveMatches ? (
          <section
            className="flex w-full max-w-[44rem] flex-col items-center gap-1 rounded-[0.75rem] border border-[var(--surface-card-border)] bg-[rgba(16,16,29,0.78)] px-4 py-2 text-center"
            aria-label={strings.TRACKER.LIVE_MATCHES}
          >
            <p className="m-0 font-shentox text-[clamp(0.78rem,1.05vw,0.9rem)] font-semibold tracking-[0.08em] uppercase text-white/75">
              {strings.TRACKER.LIVE_MATCHES}
            </p>
            <div
              className={`grid w-full gap-2 ${hasMultipleLiveMatches ? "grid-cols-2 max-md:grid-cols-1" : "grid-cols-1"}`}
            >
              {liveMatches.map((match) => {
                const teamOne = match.teamOneName ?? strings.COMMON.TBD;
                const teamTwo = match.teamTwoName ?? strings.COMMON.TBD;
                const when = formatMatchDate(match.time, locale, strings);

                return (
                  <div
                    key={match.id}
                    className={`w-full text-center ${hasMultipleLiveMatches ? "px-1 py-0.5" : "px-2 py-1"}`}
                    aria-label={`${teamOne} ${strings.COMMON.VS} ${teamTwo}`}
                  >
                    <div className="mx-auto grid w-full max-w-[31rem] grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 text-white">
                      <span className="inline-flex min-w-0 items-center justify-end gap-1.5 text-right">
                        <TeamLogo
                          teamId={match.teamOne ?? strings.COMMON.TBD}
                          alt={strings.COMMON.TEAM_LOGO_ALT.replace("{team}", teamOne)}
                          className={`${hasMultipleLiveMatches ? "h-4.5 w-4.5" : "h-5 w-5"} rounded-full object-contain`}
                          width={hasMultipleLiveMatches ? 18 : 20}
                          height={hasMultipleLiveMatches ? 18 : 20}
                          linkToTeam={false}
                        />
                        <span
                          className={`truncate font-shentox font-semibold tracking-[0.05em] uppercase ${hasMultipleLiveMatches ? "text-[clamp(0.78rem,0.95vw,0.88rem)]" : "text-[clamp(0.88rem,1.1vw,0.98rem)]"}`}
                        >
                          {teamOne}
                        </span>
                      </span>
                      <span
                        className={`justify-self-center font-shentox tracking-[0.06em] uppercase text-light-blue ${hasMultipleLiveMatches ? "text-[clamp(0.74rem,0.9vw,0.82rem)]" : "text-[clamp(0.82rem,1vw,0.92rem)]"}`}
                      >
                        {strings.COMMON.VS}
                      </span>
                      <span className="inline-flex min-w-0 items-center justify-start gap-1.5 text-left">
                        <span
                          className={`truncate font-shentox font-semibold tracking-[0.05em] uppercase ${hasMultipleLiveMatches ? "text-[clamp(0.78rem,0.95vw,0.88rem)]" : "text-[clamp(0.88rem,1.1vw,0.98rem)]"}`}
                        >
                          {teamTwo}
                        </span>
                        <TeamLogo
                          teamId={match.teamTwo ?? strings.COMMON.TBD}
                          alt={strings.COMMON.TEAM_LOGO_ALT.replace("{team}", teamTwo)}
                          className={`${hasMultipleLiveMatches ? "h-4.5 w-4.5" : "h-5 w-5"} rounded-full object-contain`}
                          width={hasMultipleLiveMatches ? 18 : 20}
                          height={hasMultipleLiveMatches ? 18 : 20}
                          linkToTeam={false}
                        />
                      </span>
                    </div>
                    {when ? (
                      <time
                        className={`mt-1 block font-jetbrains text-white/70 ${hasMultipleLiveMatches ? "text-[clamp(0.64rem,0.85vw,0.74rem)]" : "text-[clamp(0.7rem,0.95vw,0.8rem)]"}`}
                        dateTime={when.dateTime}
                      >
                        {when.date} • {when.time}
                      </time>
                    ) : null}
                    {match.link ? (
                      <CommonTwitchLink
                        href={match.link}
                        label={strings.SCHEDULE.WATCH_STREAM}
                        className={`mt-1 ${hasMultipleLiveMatches ? "text-[0.68rem]" : "text-[0.74rem]"}`}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}
      </div>
    );
  }

  const units = [
    { value: days, label: strings.TRACKER.DAYS },
    { value: hours, label: strings.TRACKER.HOURS },
    { value: minutes, label: strings.TRACKER.MINUTES },
    { value: seconds, label: strings.TRACKER.SECONDS },
  ];

  return (
    <div className="countdown-shell countdown-motion flex-col gap-3 max-md:gap-2">
      <div
        className="mx-auto flex flex-wrap justify-center gap-[clamp(1rem,2.5vw,2rem)] max-md:grid max-md:max-w-[min(100%,24rem)] max-md:grid-cols-4 max-md:gap-[0.65rem] max-md:px-1 max-[480px]:max-w-full max-[480px]:gap-2"
        role="timer"
        aria-live="polite"
      >
        {units.map((unit, index) => (
          <Fragment key={unit.label}>
            {index > 0 ? (
              <span
                className="h-[clamp(2rem,4vw,3rem)] w-0.5 self-center bg-white/45 max-md:hidden"
                aria-hidden="true"
              />
            ) : null}
            <div className="flex min-w-[clamp(4.5rem,10vw,7rem)] flex-col items-center max-md:min-w-0">
              <span className="font-jetbrains text-[clamp(2.5rem,6vw,4rem)] font-medium leading-none max-md:text-[clamp(2rem,7.5vw,2.75rem)] max-[480px]:text-[clamp(1.85rem,8vw,2.5rem)]">
                {unit.value}
              </span>
              <span className="mt-2 font-jetbrains text-[clamp(0.7rem,1.4vw,1rem)] tracking-[0.06em] opacity-[0.85]">
                {unit.label.toUpperCase()}
              </span>
            </div>
          </Fragment>
        ))}
      </div>

      {showNextMatch ? (
        <section
          className="flex w-full max-w-[44rem] flex-col items-center gap-1 rounded-[0.75rem] border border-[var(--surface-card-border)] bg-[rgba(16,16,29,0.78)] px-4 py-2 text-center"
          aria-label={strings.TRACKER.NEXT_MATCH}
        >
          <p className="m-0 font-shentox text-[clamp(0.78rem,1.05vw,0.9rem)] font-semibold tracking-[0.08em] uppercase text-white/75">
            {strings.TRACKER.NEXT_MATCH}
          </p>
          <div className="mx-auto grid w-full max-w-[31rem] grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 text-white">
            <span className="inline-flex min-w-0 items-center justify-end gap-1.5 text-right font-shentox text-[clamp(0.94rem,1.25vw,1.08rem)] font-semibold tracking-[0.05em] uppercase">
              <TeamLogo
                teamId={nextUpcomingMatch.teamOne ?? strings.COMMON.TBD}
                alt={strings.COMMON.TEAM_LOGO_ALT.replace(
                  "{team}",
                  nextUpcomingMatch.teamOneName ?? strings.COMMON.TBD,
                )}
                className="h-5 w-5 rounded-full object-contain"
                width={20}
                height={20}
                linkToTeam={false}
              />
              <span className="truncate">
                {nextUpcomingMatch.teamOneName ?? strings.COMMON.TBD}
              </span>
            </span>
            <span className="justify-self-center font-shentox text-[clamp(0.84rem,1vw,0.94rem)] tracking-[0.06em] uppercase text-light-blue">
              {strings.COMMON.VS}
            </span>
            <span className="inline-flex min-w-0 items-center justify-start gap-1.5 text-left font-shentox text-[clamp(0.94rem,1.25vw,1.08rem)] font-semibold tracking-[0.05em] uppercase">
              <span className="truncate">
                {nextUpcomingMatch.teamTwoName ?? strings.COMMON.TBD}
              </span>
              <TeamLogo
                teamId={nextUpcomingMatch.teamTwo ?? strings.COMMON.TBD}
                alt={strings.COMMON.TEAM_LOGO_ALT.replace(
                  "{team}",
                  nextUpcomingMatch.teamTwoName ?? strings.COMMON.TBD,
                )}
                className="h-5 w-5 rounded-full object-contain"
                width={20}
                height={20}
                linkToTeam={false}
              />
            </span>
          </div>
          <time
            className="font-jetbrains text-[clamp(0.72rem,0.95vw,0.84rem)] text-white/70"
            dateTime={nextMatchTime.dateTime}
          >
            {nextMatchTime.weekday}, {nextMatchTime.date} • {nextMatchTime.time}
          </time>
        </section>
      ) : null}
    </div>
  );
};
